import { notifications } from '@mantine/notifications';

import { useMutation, useQueryClient } from '@tanstack/react-query';

import useApi from '@/hooks/useApi/useApi';
import { API_URL } from '@/lib/apiUrl';
import { queryKeys } from '@/lib/queryKeys';
import {
  PlanningCache,
  applyIntervalDelete,
  applyLockedTaskDateRollup,
  findTaskIdForInterval,
} from '@/modules/Planning/hooks/usePlanningProjects/planningCacheUtils';
import { Interval } from '@/types/types';

const useDeleteInterval = (optimisticPlanningUpdate: boolean = false) => {
  const { axiosInstance } = useApi();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async (intervalId: Interval['id']) => {
      await axiosInstance.delete(`${API_URL}/api/v1/intervals/${intervalId}`);
    },

    onMutate: async (intervalId) => {
      if (!optimisticPlanningUpdate) return;

      const queryKey = queryKeys.planningProjects._def;
      await queryClient.cancelQueries({ queryKey });
      const snapshot = queryClient.getQueriesData<PlanningCache>({ queryKey });

      queryClient.setQueriesData<PlanningCache>({ queryKey }, (old) => {
        if (!old) return old;
        const taskId = findTaskIdForInterval(old, intervalId);
        const deleted = applyIntervalDelete(old, intervalId);
        return applyLockedTaskDateRollup(deleted, taskId);
      });

      return { snapshot };
    },

    onError: (_err, _vars, context) => {
      context?.snapshot?.forEach(([key, data]) => {
        queryClient.setQueryData(key, data);
      });
      notifications.show({
        title: 'Fejl',
        message: 'Kunne ikke slette interval',
        color: 'red',
      });
    },

    onSuccess: () => {
      notifications.show({
        title: 'Succes',
        message: 'Interval blev slettet',
        color: 'green',
      });
    },

    onSettled: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.accountAvailabilities._def,
      });
    },
  });
};

export default useDeleteInterval;
